import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { OfflineProvider } from './context/OfflineContext';
import { Toaster } from './components/ui/sonner';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Inspections from './pages/Inspections';
import NewInspection from './pages/NewInspection';
import InspectionDetail from './pages/InspectionDetail';
import Checklists from './pages/Checklists';
import ChecklistEditor from './pages/ChecklistEditor';
import Users from './pages/Users';
import SuperAdmin from './pages/SuperAdmin';
import Statistics from './pages/Statistics';
import AGB from './pages/AGB';

// Register Service Worker
function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/service-worker.js')
        .then(registration => {
          console.log('SW registered:', registration);
        })
        .catch(error => {
          console.log('SW registration failed:', error);
        });
    });
  }
}

// Protected Route Component
function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Public Route Component (redirects if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />
      
      {/* Super-Admin Route (öffentlich, hat eigenes Login) */}
      <Route path="/super-admin" element={<SuperAdmin />} />
      
      {/* AGB Route (öffentlich) */}
      <Route path="/agb" element={<AGB />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/inspections" element={
        <ProtectedRoute>
          <Inspections />
        </ProtectedRoute>
      } />
      <Route path="/inspections/new" element={
        <ProtectedRoute>
          <NewInspection />
        </ProtectedRoute>
      } />
      <Route path="/inspections/:id" element={
        <ProtectedRoute>
          <InspectionDetail />
        </ProtectedRoute>
      } />
      <Route path="/checklists" element={
        <ProtectedRoute>
          <Checklists />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin/checklists/new" element={
        <ProtectedRoute adminOnly>
          <ChecklistEditor />
        </ProtectedRoute>
      } />
      <Route path="/admin/checklists/:id" element={
        <ProtectedRoute adminOnly>
          <ChecklistEditor />
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute adminOnly>
          <Users />
        </ProtectedRoute>
      } />
      <Route path="/statistics" element={
        <ProtectedRoute>
          <Statistics />
        </ProtectedRoute>
      } />

      {/* Redirects */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  // Register service worker on mount
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <div className="dark">
      <BrowserRouter>
        <AuthProvider>
          <OfflineProvider>
            <AppRoutes />
            <Toaster />
          </OfflineProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
